import { JobType } from "@app/jobs";
import { TooltipWrapper } from "@app/shared";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton, QESRadioButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { FC, Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { JobStastiticsContext } from "./JobStatisticsContext";
import { ChartColumnEnum, FilteringOptionsState, GraphTypeEnum, SqlAggregationTypeEnum, UserDefinedChartParameters } from "./types";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

export const NewUserDefinedChartPanelContent: FC<React.PropsWithChildren<unknown>> = () => {
    const [jobTypes, setJobTypes] = useState<JobType[]>([]);
    const [xAxisOptions, setXAxisOptions] = useState<ChartColumnEnum[]>([]);
    const [filteringOptions, setFilteringOptions] = useState<ChartColumnEnum[]>([]);
    const flyoutContext = useContext(FlyoutContext);
    const yAxisOptions = useRef<GraphTypeEnum[]>(Object.values(GraphTypeEnum)).current;

    const [chartName, setChartName] = useState("");
    const [jobType, setJobType] = useState<JobType>();
    const [xOption, setXOption] = useState<ChartColumnEnum>();
    const [yOption, setYOption] = useState<GraphTypeEnum>();
    const [selectedFilters, setSelectedFilters] = useState<Map<ChartColumnEnum, string>>(new Map<ChartColumnEnum, string>());
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const jobStatisticsContext = useContext(JobStastiticsContext);

    useEffect(() => {
        jobStatisticsContext.getJobTypes().then((newJobTypes) => {
            setJobTypes(newJobTypes);
            setJobType(newJobTypes[0]);
        });
    }, []);

    useEffect(() => {
        if (!jobType) return;
        jobStatisticsContext.getXAxisOptions(jobType).then((newXOptions) => {
            setXAxisOptions(newXOptions);
            setXOption(xOption || newXOptions[0]);
        });
    }, [jobType]);

    useEffect(() => {
        if (!jobType) return;
        jobStatisticsContext.getFilteringOptions(jobType).then((newFilteringOptions) => {
            setSelectedFilters(new Map<ChartColumnEnum, string>());
            setFilteringOptions(newFilteringOptions);
        });
    }, [jobType]);

    const makeOptionElements = (entry: string[]) => {
        return entry.map((item) => <option value={item} key={item}>{item}</option>);
    }

    const jobTypeOptionElements = useMemo(() => {
        return makeOptionElements(jobTypes);
    }, [jobTypes]);

    const xAxisOptionElements = useMemo(() => {
        return makeOptionElements(xAxisOptions);
    }, [xAxisOptions]);

    const yAxisOptionElements = useMemo(() => {
        // ensure the default selection isn't "Unknown"
        setYOption(yAxisOptions[1]);
        // slice 1 to remove "Unknown"
        return makeOptionElements(yAxisOptions.slice(1));
    }, [yAxisOptions]);

    const filteringOptionElements = useMemo(() => {
        return (
            <div className="col-10 mt-3 row">
                <div className="col-12 row">
                    <span className="d-inline-block qes-text-input__label col-6">Filtering Options</span>
                    <span className="d-inline-block col-2">
                        <TooltipWrapper text="Filter to jobs that do not include this option">
                            <QesIcon iconName={QesIcons.FontAwesome.MinusCircle} testId="new-user-defined-chart-panel-content__minus-circle" width="16" height="14" description="minus sign acting as a tooltip trigger" />
                        </TooltipWrapper>
                    </span>
                    <span className="d-inline-block col-2">
                        <TooltipWrapper text="Do not filter on this option">
                            <QesIcon iconName={QesIcons.FontAwesome.Circle} testId="new-user-defined-chart-panel-content__circle" width="16" height="14" description="empty circle acting as a tooltip trigger" />
                        </TooltipWrapper>
                    </span>
                    <span className="d-inline-block col-2">
                        <TooltipWrapper text="Filter to jobs that do include this option">
                            <QesIcon iconName={QesIcons.FontAwesome.PlusCircle} testId="new-user-defined-chart-panel-content__plus-circle" width="16" height="14" description="plus sign acting as a tooltip trigger" />
                        </TooltipWrapper>
                    </span>
                </div>
                {
                    filteringOptions.map((option) => {
                        return (
                            <label key={option} className="col-12 row">
                                <span className="col-6 d-inline-block pl-4">
                                    {option}
                                </span>
                                <span className="col-2 d-inline-block">
                                    <QESRadioButton
                                        label=""
                                        // always disabled because the api currently cannot handle it
                                        disabled={true || selectedFilters.size >= 2 && !selectedFilters.has(option)}
                                        name={option}
                                        checked={selectedFilters.get(option) === FilteringOptionsState.Negative.toString()}
                                        value={FilteringOptionsState.Negative}
                                        onChange={(e) => updateFilteringOptions(option, e.target.value)}
                                        className="qes-radio"
                                        testId={`new-user-defined-chart__negative--${option}`}
                                    />
                                </span>
                                <span className="col-2 d-inline-block">
                                    <QESRadioButton
                                        label=""
                                        name={option}
                                        checked={!selectedFilters.has(option)}
                                        value=""
                                        onChange={(e) => updateFilteringOptions(option, e.target.value)}
                                        className="qes-radio"
                                        testId={`new-user-defined-chart__neutral--${option}`}
                                    />
                                </span>
                                <span className="col-2 d-inline-block">
                                    <QESRadioButton
                                        label=""
                                        disabled={selectedFilters.size >= 2 && !selectedFilters.has(option)}
                                        name={option}
                                        checked={selectedFilters.get(option) === FilteringOptionsState.Positive.toString()}
                                        value={FilteringOptionsState.Positive}
                                        onChange={(e) => updateFilteringOptions(option, e.target.value)}
                                        className="qes-radio"
                                        testId={`new-user-defined-chart__positive--${option}`}
                                    />
                                </span>
                            </label>
                        );
                    })
                }
            </div >
        );
    }, [filteringOptions, selectedFilters]);

    const updateFilteringOptions = (field: ChartColumnEnum, value: string | null) => {
        const newSelectedFilters = new Map(selectedFilters);
        if (value) {
            newSelectedFilters.set(field, value);
        } else {
            newSelectedFilters.delete(field);
        }
        setSelectedFilters(newSelectedFilters);
    }

    const submitChartDetails = () => {
        const chartDetails: UserDefinedChartParameters = {
            dataPoints: [],
            filterColumns: Array.from(selectedFilters.entries()).filter((sf) => sf[1] === FilteringOptionsState.Positive.toString()).map((sf) => sf[0]),
            jobType: jobType,
            name: chartName,
            sqlAggregationType: yOption == GraphTypeEnum.Runs ? SqlAggregationTypeEnum.Count : SqlAggregationTypeEnum.Avg,
            xAxis: xOption,
            yAxis: yOption,
        };
        submitButtonRef.current.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
            <span class="sr-only">Loading...</span>
        </div>`;
        // let the context worry about handling the response, but don't close the flyout until we have a response
        jobStatisticsContext.createChart(chartDetails).then(() => {
            flyoutContext.handleCloseFlyout();
        });
    }

    return (
        <Fragment>
            <div className="col-6">
                <QESTextInput label="Chart Name" testId="define-chart__name" onChange={(e) => setChartName(e.target.value)} value={chartName} maxLength={200} />
                <QESSelect label="Job Type" containerClassName="mt-3" testId="define-chart__job-type" onChange={(e) => { setJobType(e.target.value as JobType) }} value={jobType}>
                    {jobTypeOptionElements}
                </QESSelect>

                <QESSelect label="Metric (X Axis)" containerClassName="mt-3" testId="define-chart__x-axis" onChange={(e) => { setXOption(e.target.value as ChartColumnEnum) }} value={xOption}>
                    {xAxisOptionElements}
                </QESSelect>

                <QESSelect label="Measurement (Y Axis)" containerClassName="mt-3" testId="define-chart__y-axis" onChange={(e) => { setYOption(e.target.value as GraphTypeEnum) }} value={yOption}>
                    {yAxisOptionElements}
                </QESSelect>
            </div>
            {filteringOptionElements}
            <FlyoutFooter
                submitButton={<QESButton qesType="primary" qesTestId="define-chart__submit" disabled={chartName === ""} onClick={(submitChartDetails)} qesRef={submitButtonRef}>Submit</QESButton>}
            />
        </Fragment>
    );
}